import { z } from 'zod'

import { BillingAuthorizationSchema } from './billing-authorization'
import { BillingScheduleLineSchema } from './billing-schedule'
import { ContractSchema } from './contract'
import { EntitySchema } from './entity'
import { InvoiceSchema } from './invoice'
import { PaginationMetaSchema } from './pagination'
import { ProjectSchema } from './project'

/**
 * Sentinel value indicating the user explicitly chose to leave a field empty
 * (e.g. contract type) during an import. Stored in explicitMappings; the
 * backend strips it before writing to the database.
 */
export const COMMERCIAL_OPS_IMPORT_LEAVE_EMPTY = '__leave_empty__'
export const COMMERCIAL_OPS_IMPORT_CREATE_NEW = '__create_new__'

export const CommercialOpsImportSourceKindSchema = z.enum([
  'CONTRACT_SOW',
  'BILLING_AUTHORIZATION',
  'BILLING_SCHEDULE',
  'DEAL_PIPELINE',
  'PROSPECT_OR_NOTES',
  'IGNORE',
])

export type CommercialOpsImportSourceKind = z.infer<
  typeof CommercialOpsImportSourceKindSchema
>

export const CommercialOpsImportTargetTypeSchema = z.enum([
  'CONTRACT',
  'BILLING_PROFILE',
  'BILLING_AUTHORIZATION',
  'BILLING_SCHEDULE_LINE',
  'DEAL',
  'NOTE',
  'NONE',
])

export type CommercialOpsImportTargetType = z.infer<
  typeof CommercialOpsImportTargetTypeSchema
>

export const CommercialOpsImportActionSchema = z.enum([
  'CREATE',
  'UPDATE',
  'SKIP',
  'AMBIGUOUS',
])

export type CommercialOpsImportAction = z.infer<
  typeof CommercialOpsImportActionSchema
>

export const CommercialOpsImportSheetMappingSchema = z.record(
  CommercialOpsImportSourceKindSchema,
)

export type CommercialOpsImportSheetMapping = z.infer<
  typeof CommercialOpsImportSheetMappingSchema
>

export const CommercialOpsImportEnumFieldKeySchema = z.enum([
  'contractStatus',
  'sowStatus',
  'renewalStatus',
  'authorizationType',
  'authorizationStatus',
  'scheduleCadence',
  'scheduleStatus',
])

export type CommercialOpsImportEnumFieldKey = z.infer<
  typeof CommercialOpsImportEnumFieldKeySchema
>

export const CommercialOpsImportIssueSchema = z.object({
  code: z.string(),
  severity: z.enum(['info', 'warning', 'blocking']),
  message: z.string(),
})

export type CommercialOpsImportIssue = z.infer<
  typeof CommercialOpsImportIssueSchema
>

export const CommercialOpsImportParserDiagnosticSchema = z.object({
  code: z.string(),
  message: z.string(),
  severity: z.enum(['warning', 'blocking']).default('blocking'),
  sheetName: z.string().optional(),
  rowNumber: z.number().optional(),
  columnName: z.string().optional(),
})

export type CommercialOpsImportParserDiagnostic = z.infer<
  typeof CommercialOpsImportParserDiagnosticSchema
>

export const CommercialOpsImportDryRunRequestSchema = z.object({
  fileDocumentId: z.string().uuid(),
  sheetMappings: CommercialOpsImportSheetMappingSchema.optional(),
  fieldMappings: z.record(z.record(z.string())).optional(),
  explicitMappings: z
    .object({
      entities: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_CREATE_NEW),
          ]),
        )
        .optional(),
      projects: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_LEAVE_EMPTY),
          ]),
        )
        .optional(),
      contracts: z.record(z.string().uuid()).optional(),
      contractTypes: z.record(z.string()).optional(),
      billingCategories: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_CREATE_NEW),
          ]),
        )
        .optional(),
      billingAuthorizations: z.record(z.string().uuid()).optional(),
      deals: z.record(z.string().uuid()).optional(),
      stages: z.record(z.string()).optional(),
      users: z.record(z.string().uuid()).optional(),
      contacts: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_LEAVE_EMPTY),
          ]),
        )
        .optional(),
      enumValues: z
        .record(CommercialOpsImportEnumFieldKeySchema, z.record(z.string()))
        .optional(),
    })
    .optional(),
})

export type CommercialOpsImportDryRunRequest = z.infer<
  typeof CommercialOpsImportDryRunRequestSchema
>

export const CommercialOpsImportRowResultSchema = z.object({
  id: z.string(),
  sheetName: z.string(),
  rowNumber: z.number(),
  sourceKind: CommercialOpsImportSourceKindSchema,
  targetType: CommercialOpsImportTargetTypeSchema,
  action: CommercialOpsImportActionSchema,
  source: z.record(z.unknown()),
  normalized: z.record(z.unknown()).default({}),
  existing: z.record(z.unknown()).optional(),
  errors: z.array(z.string()).default([]),
  warnings: z.array(z.string()).default([]),
  issues: z.array(CommercialOpsImportIssueSchema).default([]),
  matchCandidates: z
    .array(
      z.object({
        type: z.enum([
          'ENTITY',
          'PROJECT',
          'CONTRACT',
          'BILLING_CATEGORY',
          'BILLING_AUTHORIZATION',
          'DEAL',
          'STAGE',
          'USER',
          'CONTACT',
        ]),
        sourceValue: z.string(),
        candidates: z.array(
          z.object({
            id: z.string().uuid(),
            label: z.string(),
            secondaryLabel: z.string().optional(),
          }),
        ),
        warning: z.string().optional(),
        allowGlobalOptions: z.boolean().optional(),
      }),
    )
    .default([]),
  enumCandidates: z
    .array(
      z.object({
        field: CommercialOpsImportEnumFieldKeySchema,
        sourceValue: z.string(),
      }),
    )
    .default([]),
})

export type CommercialOpsImportRowResult = z.infer<
  typeof CommercialOpsImportRowResultSchema
>

const CommercialOpsImportMatchOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  secondaryLabel: z.string().optional(),
})

export const CommercialOpsImportMatchTypeSchema = z.enum([
  'ENTITY',
  'PROJECT',
  'CONTRACT',
  'BILLING_CATEGORY',
  'BILLING_AUTHORIZATION',
  'DEAL',
  'STAGE',
  'USER',
  'CONTACT',
])

export type CommercialOpsImportMatchType = z.infer<
  typeof CommercialOpsImportMatchTypeSchema
>

const CommercialOpsImportMatchOptionsSchema = z
  .object({
    ENTITY: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    PROJECT: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    CONTRACT: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    BILLING_CATEGORY: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    BILLING_AUTHORIZATION: z
      .array(CommercialOpsImportMatchOptionSchema)
      .default([]),
    DEAL: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    STAGE: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    USER: z.array(CommercialOpsImportMatchOptionSchema).default([]),
    CONTACT: z.array(CommercialOpsImportMatchOptionSchema).default([]),
  })
  .default({
    ENTITY: [],
    PROJECT: [],
    CONTRACT: [],
    BILLING_CATEGORY: [],
    BILLING_AUTHORIZATION: [],
    DEAL: [],
    STAGE: [],
    USER: [],
    CONTACT: [],
  })

export type CommercialOpsImportMatchOptions = z.infer<
  typeof CommercialOpsImportMatchOptionsSchema
>

export const CommercialOpsImportEnumFieldDefinitionSchema = z.object({
  key: CommercialOpsImportEnumFieldKeySchema,
  label: z.string(),
  module: z.enum(['CONTRACT', 'BILLING_AUTHORIZATION', 'BILLING_SCHEDULE']),
  defaultValue: z.string(),
  options: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    }),
  ),
})

export type CommercialOpsImportEnumFieldDefinition = z.infer<
  typeof CommercialOpsImportEnumFieldDefinitionSchema
>

export const CommercialOpsImportDryRunResultSchema = z.object({
  fileDocumentId: z.string().uuid(),
  fileName: z.string().optional(),
  sheets: z.array(
    z.object({
      name: z.string(),
      detectedKind: CommercialOpsImportSourceKindSchema,
      rowCount: z.number(),
      diagnostics: z
        .array(CommercialOpsImportParserDiagnosticSchema)
        .default([]),
    }),
  ),
  parserDiagnostics: z
    .array(CommercialOpsImportParserDiagnosticSchema)
    .default([]),
  rows: z.array(CommercialOpsImportRowResultSchema),
  matchOptions: CommercialOpsImportMatchOptionsSchema,
  enumFieldDefinitions: z
    .array(CommercialOpsImportEnumFieldDefinitionSchema)
    .default([]),
  summary: z.object({
    totalRows: z.number(),
    creates: z.number(),
    updates: z.number(),
    skips: z.number(),
    ambiguous: z.number(),
    errors: z.number(),
    warnings: z.number(),
  }),
  previewFingerprint: z.string(),
})

export type CommercialOpsImportDryRunResult = z.infer<
  typeof CommercialOpsImportDryRunResultSchema
>

export const CommercialOpsImportCommitRequestSchema = z.object({
  fileDocumentId: z.string().uuid(),
  sheetMappings: CommercialOpsImportSheetMappingSchema.optional(),
  fieldMappings: z.record(z.record(z.string())).optional(),
  explicitMappings: z
    .object({
      entities: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_CREATE_NEW),
          ]),
        )
        .optional(),
      projects: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_LEAVE_EMPTY),
          ]),
        )
        .optional(),
      contracts: z.record(z.string().uuid()).optional(),
      contractTypes: z.record(z.string()).optional(),
      billingCategories: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_CREATE_NEW),
          ]),
        )
        .optional(),
      billingAuthorizations: z.record(z.string().uuid()).optional(),
      deals: z.record(z.string().uuid()).optional(),
      stages: z.record(z.string()).optional(),
      users: z.record(z.string().uuid()).optional(),
      contacts: z
        .record(
          z.union([
            z.string().uuid(),
            z.literal(COMMERCIAL_OPS_IMPORT_LEAVE_EMPTY),
          ]),
        )
        .optional(),
      enumValues: z
        .record(CommercialOpsImportEnumFieldKeySchema, z.record(z.string()))
        .optional(),
    })
    .optional(),
  rowIds: z.array(z.string()).optional(),
  previewFingerprint: z.string().optional(),
  reviewedRows: z.array(CommercialOpsImportRowResultSchema).optional(),
})

export type CommercialOpsImportCommitRequest = z.infer<
  typeof CommercialOpsImportCommitRequestSchema
>

export const CommercialOpsImportCommitResultSchema = z.object({
  jobId: z.string().uuid().optional(),
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.number(),
  createdIds: z.record(z.array(z.string().uuid())),
  updatedIds: z.record(z.array(z.string().uuid())).default({}),
  errorRows: z.array(CommercialOpsImportRowResultSchema).default([]),
})

export type CommercialOpsImportCommitResult = z.infer<
  typeof CommercialOpsImportCommitResultSchema
>

export const CommercialOpsImportRollbackResultSchema = z.object({
  jobId: z.string().uuid(),
  rolledBack: z.number(),
  skipped: z.number(),
  errors: z.array(z.string()).default([]),
})

export type CommercialOpsImportRollbackResult = z.infer<
  typeof CommercialOpsImportRollbackResultSchema
>

export const CommercialOpsImportJobSummarySchema = z.object({
  id: z.string().uuid(),
  fileName: z.string().optional(),
  status: z.string(),
  totalRecords: z.number(),
  processedRecords: z.number(),
  createdCount: z.number(),
  updatedCount: z.number(),
  skippedCount: z.number(),
  errorCount: z.number(),
  createdAt: z.string().optional(),
  completedAt: z.string().optional(),
  rolledBackAt: z.string().optional(),
})

export type CommercialOpsImportJobSummary = z.infer<
  typeof CommercialOpsImportJobSummarySchema
>

export const CommercialOpsWindowQuerySchema = z.object({
  windowDays: z.coerce.number().int().positive().max(365).default(30),
  limit: z.coerce.number().int().positive().max(100).default(10),
  missingAuthorizationPage: z.coerce.number().int().positive().default(1),
  expiringAuthorizationsPage: z.coerce.number().int().positive().default(1),
  nearlyExhaustedAuthorizationsPage: z.coerce
    .number()
    .int()
    .positive()
    .default(1),
  sowsAwaitingClientPage: z.coerce.number().int().positive().default(1),
  upcomingRenewalsPage: z.coerce.number().int().positive().default(1),
  readyScheduleLinesPage: z.coerce.number().int().positive().default(1),
})

export type CommercialOpsWindowQuery = z.infer<
  typeof CommercialOpsWindowQuerySchema
>

export const InvoiceReadinessIssueSchema = z.object({
  code: z.enum([
    'MISSING_REQUIRED_AUTHORIZATION',
    'AUTHORIZATION_EXPIRED',
    'AUTHORIZATION_NEARLY_EXHAUSTED',
    'READY_BILLING_LINES',
  ]),
  message: z.string(),
  severity: z.enum(['info', 'warning', 'blocking']).default('warning'),
})

export type InvoiceReadinessIssue = z.infer<typeof InvoiceReadinessIssueSchema>

export const CommercialOpsDashboardSchema = z.object({
  windowDays: z.number(),
  generatedAt: z.date(),
  metrics: z.object({
    missingAuthorizationInvoices: z.number(),
    expiringAuthorizations: z.number(),
    nearlyExhaustedAuthorizations: z.number(),
    sowsAwaitingClient: z.number(),
    upcomingRenewals: z.number(),
    readyScheduleLines: z.number(),
    // Org-wide hide rule (Phase 2.2): true when any active billing profile
    // requires client authorization.
    anyProfileRequiresAuthorization: z.boolean().optional().default(false),
  }),
  missingAuthorizationInvoices: z.array(InvoiceSchema),
  expiringAuthorizations: z.array(BillingAuthorizationSchema),
  nearlyExhaustedAuthorizations: z.array(BillingAuthorizationSchema),
  sowsAwaitingClient: z.array(ContractSchema),
  upcomingRenewals: z.array(ContractSchema),
  readyScheduleLines: z.array(BillingScheduleLineSchema),
  pagination: z.object({
    missingAuthorizationInvoices: PaginationMetaSchema,
    expiringAuthorizations: PaginationMetaSchema,
    nearlyExhaustedAuthorizations: PaginationMetaSchema,
    sowsAwaitingClient: PaginationMetaSchema,
    upcomingRenewals: PaginationMetaSchema,
    readyScheduleLines: PaginationMetaSchema,
  }),
})

export type CommercialOpsDashboard = z.infer<
  typeof CommercialOpsDashboardSchema
>

export const ClientCommercialSummarySchema = z.object({
  entity: EntitySchema,
  activeContracts: z.array(ContractSchema),
  billingAuthorizations: z.array(BillingAuthorizationSchema),
  upcomingBilling: z.array(BillingScheduleLineSchema),
  recentInvoices: z.array(InvoiceSchema),
  renewalAlerts: z.array(ContractSchema),
  invoiceReadinessIssues: z.array(InvoiceReadinessIssueSchema),
})

export type ClientCommercialSummary = z.infer<
  typeof ClientCommercialSummarySchema
>

export const ContractCommercialSummarySchema = z.object({
  contract: ContractSchema,
  entity: EntitySchema.nullable(),
  project: ProjectSchema.nullable(),
  linkedAuthorizations: z.array(BillingAuthorizationSchema),
  scheduledBilling: z.array(BillingScheduleLineSchema),
  recentInvoices: z.array(InvoiceSchema),
  invoiceReadinessIssues: z.array(InvoiceReadinessIssueSchema),
})

export type ContractCommercialSummary = z.infer<
  typeof ContractCommercialSummarySchema
>

export const ProjectCommercialSummaryQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(100).default(10),
  projectContractsPage: z.coerce.number().int().positive().default(1),
  clientWideContractsPage: z.coerce.number().int().positive().default(1),
  authorizationsPage: z.coerce.number().int().positive().default(1),
  scheduledBillingPage: z.coerce.number().int().positive().default(1),
  invoicesPage: z.coerce.number().int().positive().default(1),
})

export type ProjectCommercialSummaryQuery = z.infer<
  typeof ProjectCommercialSummaryQuerySchema
>

export const ProjectCommercialSummarySchema = z.object({
  project: ProjectSchema,
  entity: EntitySchema.nullable(),
  projectContracts: z.array(ContractSchema),
  clientWideContracts: z.array(ContractSchema),
  eligibleAuthorizations: z.array(BillingAuthorizationSchema),
  scheduledBilling: z.array(BillingScheduleLineSchema),
  recentInvoices: z.array(InvoiceSchema),
  invoiceReadinessIssues: z.array(InvoiceReadinessIssueSchema),
  pagination: z.object({
    projectContracts: PaginationMetaSchema,
    clientWideContracts: PaginationMetaSchema,
    eligibleAuthorizations: PaginationMetaSchema,
    scheduledBilling: PaginationMetaSchema,
    recentInvoices: PaginationMetaSchema,
  }),
})

export type ProjectCommercialSummary = z.infer<
  typeof ProjectCommercialSummarySchema
>
